/**
 * Client Overrides Tab Component
 *
 * Displays and manages per-attribute SLA overrides for clients.
 * Allows creating overrides that modify specific SLA attributes.
 *
 * @module pages/Settings/SLAConfiguration/ClientOverridesTab
 */

import React, { useState, useEffect } from 'react';
import { ClientSLAOverride } from '@shared/types/sla';
import slaService from '@services/slaService';
import OverrideForm from '@components/SLA/OverrideForm';
import OverrideList from '@components/SLA/OverrideList';

interface ClientOverridesTabProps {
  /** Optional callback when override is created/updated/deleted */
  onOverrideChanged?: () => void;
}

/**
 * Client Overrides Tab - Manage per-attribute SLA overrides
 */
const ClientOverridesTab: React.FC<ClientOverridesTabProps> = ({ onOverrideChanged }) => {
  const [overrides, setOverrides] = useState<ClientSLAOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [editingOverride, setEditingOverride] = useState<ClientSLAOverride | null>(null);

  useEffect(() => {
    loadOverrides();
  }, []);

  const loadOverrides = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await slaService.getOverrides();
      setOverrides(data);
    } catch (err) {
      console.error('Failed to load overrides:', err);
      setError(err instanceof Error ? err.message : 'Failed to load overrides');
    } finally {
      setLoading(false);
    }
  };

  const handleOverrideChanged = () => {
    loadOverrides();
    setShowOverrideForm(false);
    setEditingOverride(null);
    if (onOverrideChanged) {
      onOverrideChanged();
    }
  };

  const handleEdit = (override: ClientSLAOverride) => {
    setEditingOverride(override);
    setShowOverrideForm(true);
  };

  const handleDelete = async (overrideId: string) => {
    if (!confirm('Are you sure you want to delete this override?')) {
      return;
    }

    try {
      await slaService.deleteOverride(overrideId);
      handleOverrideChanged();
    } catch (err) {
      console.error('Failed to delete override:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete override');
    }
  };

  const handleCloseForm = () => {
    setShowOverrideForm(false);
    setEditingOverride(null);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-gray-500'>Loading overrides...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-md bg-red-50 p-4'>
        <div className='flex'>
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-red-800'>Error Loading Overrides</h3>
            <div className='mt-2 text-sm text-red-700'>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-medium text-gray-900'>Client SLA Overrides</h3>
          <p className='mt-1 text-sm text-gray-500'>
            Override specific SLA attributes for individual clients. Overrides are applied on top of
            the base tier assignment.
          </p>
        </div>
        <button
          type='button'
          onClick={() => setShowOverrideForm(true)}
          className='inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500'
        >
          New Override
        </button>
      </div>

      {/* Overrides List */}
      <OverrideList overrides={overrides} onEdit={handleEdit} onDelete={handleDelete} />

      {/* Override Form Modal */}
      {showOverrideForm && (
        <OverrideForm
          override={editingOverride || undefined}
          onOverrideChanged={handleOverrideChanged}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default ClientOverridesTab;
